import { useEffect, useRef } from 'react'
import { Box, Typography, Skeleton } from '@mui/material'
import { styled } from '@mui/material/styles'
import LargeCard from '../../components/cards/large-card'
import ContentContainer from '../layout/content-container'
import { MediaGridItem } from '../../types/Media'

type Props = {
  title: string
  subtitle?: string
  items: MediaGridItem[]
  loading?: boolean
  error?: string | null
  hasMore?: boolean
  onLoadMore?: () => void
}

const PageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}))

const Header = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}))

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 28,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}))

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 15,
}))

const Grid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: theme.spacing(3),
  justifyItems: 'center',
}))

const NoResults = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(8),
  color: theme.palette.text.secondary,
}))

const LoadingMoreWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(3),
}))

const LoadMoreSentinel = styled('div')(() => ({
  height: 1,
}))

const PosterSkeleton = styled(Skeleton)(({ theme }) => ({
  width: 220,
  height: 330,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  transform: 'none',
  backgroundColor: theme.palette.action.hover,
}))

const TitleSkeleton = styled(Skeleton)({
  width: '80%',
  height: 20,
  transform: 'none',
})

const SubtitleSkeleton = styled(Skeleton)({
  width: '50%',
  height: 16,
  transform: 'none',
})

const LoadingMorePosterSkeleton = styled(PosterSkeleton)({
  marginLeft: 8,
  marginRight: 8,
})

const CardSkeleton = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 220,
  justifySelf: 'center',
  '> * + *': {
    marginTop: theme.spacing(1),
  },
}))

const MediaGridPage = ({
  title,
  subtitle,
  items,
  loading,
  error,
  hasMore,
  onLoadMore,
}: Props) => {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!onLoadMore || !hasMore) return

    const target = loadMoreRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && !loading) {
          onLoadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [onLoadMore, hasMore, loading, items.length])

  if (loading && items.length === 0) {
    return (
      <ContentContainer>
        <PageWrapper>
          <Header>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </Header>

          <Grid>
            {Array.from({ length: 12 }).map((_, index) => (
              <CardSkeleton key={index}>
                <PosterSkeleton variant='rectangular' />
                <TitleSkeleton variant='text' />
                <SubtitleSkeleton variant='text' />
              </CardSkeleton>
            ))}
          </Grid>
        </PageWrapper>
      </ContentContainer>
    )
  }

  if (error) {
    return (
      <ContentContainer>
        <PageWrapper>
          <Typography color='error'>{error}</Typography>
        </PageWrapper>
      </ContentContainer>
    )
  }

  return (
    <ContentContainer>
      <PageWrapper>
        <Header>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Header>

        {items.length > 0 ? (
          <>
            <Grid>
              {items.map((item) => (
                <LargeCard
                  key={`${item.type}-${item.id}`}
                  id={item.id}
                  type={item.type}
                  title={item.title}
                  year={item.year}
                  rating={item.rating}
                  posterUrl={item.posterUrl}
                  genres={item.genres}
                />
              ))}
            </Grid>

            {loading && items.length > 0 && (
              <LoadingMoreWrapper>
                {Array.from({ length: 3 }).map((_, index) => (
                  <LoadingMorePosterSkeleton
                    key={`loading-${index}`}
                    variant='rectangular'
                  />
                ))}
              </LoadingMoreWrapper>
            )}

            {hasMore && onLoadMore && <LoadMoreSentinel ref={loadMoreRef} />}
          </>
        ) : (
          <NoResults>
            <Typography variant='h6'>No results found</Typography>
            <Typography variant='body2'>Try adjusting your filters</Typography>
          </NoResults>
        )}
      </PageWrapper>
    </ContentContainer>
  )
}

export default MediaGridPage
